import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import RiderLayout from "./RiderLayout";
import { User, Star, MapPin, Search, Volume2, Bell, ShieldCheck, Fingerprint } from "lucide-react";
import axios from "axios";
import API from "@/utils/api";
import { toast } from "sonner";
import { registerBiometry, isBiometryAvailable } from "@/utils/biometry";

export default function RiderProfile() {
    const [rider, setRider] = useState(null);
    const [biometrySupported, setBiometrySupported] = useState(false);
    const [biometryEnabled, setBiometryEnabled] = useState(false);

    useEffect(() => {
        const localRider = JSON.parse(localStorage.getItem("rider_data") || "{}");
        setRider(localRider);

        // Verificar si el dispositivo soporta biometría
        isBiometryAvailable().then(supported => {
            setBiometrySupported(supported);
        });

        // Verificar si ya tiene biometría configurada localmente (preferencia)
        const isBiometricSet = localStorage.getItem("rider_biometric_enabled") === "true";
        setBiometryEnabled(isBiometricSet);
    }, []);

    const handleToggleBiometry = async () => {
        if (!biometrySupported) {
            toast.error("Tu dispositivo no soporta biometría o no está habilitada.");
            return;
        }

        if (biometryEnabled) {
            // Desactivar biometría (solo localmente por ahora por sencillez)
            localStorage.setItem("rider_biometric_enabled", "false");
            setBiometryEnabled(false);
            toast.success("Inicio biométrico desactivado");
            return;
        }

        try {
            toast.info("Por favor, confirma tu identidad en el dispositivo...");
            const res = await registerBiometry();
            if (res.status === "ok") {
                localStorage.setItem("rider_biometric_enabled", "true");
                setBiometryEnabled(true);
                toast.success("¡Huella/Cara registrada con éxito!");
            }
        } catch (error) {
            toast.error("Error al registrar biometría. Asegúrate de tener un PIN/Huella en tu móvil.");
        }
    };

    const toggleStatus = async () => {
        toast.info("Función de cambio de estado en desarrollo (Online por defecto)");
    };

    const testSound = () => {
        const audio = new Audio("/sounds/conexo_alert.mp3");
        audio.play().catch(e => {
            console.error(e);
            toast.error("El navegador bloqueó el sonido. Pulsa en la pantalla una vez para habilitarlo.");
        });
        toast.success("Reproduciendo sonido de prueba...", { icon: "🔔" });
    };

    if (!rider) return null;

    return (
        <RiderLayout>
            <div className="p-4 space-y-6">
                {/* Profile Header */}
                <div className="relative pt-12 pb-6 flex flex-col items-center bg-white/5 border border-white/10 rounded-3xl overflow-hidden shadow-2xl backdrop-blur-md">
                    <div className="absolute top-0 w-full h-1/2 bg-gradient-to-b from-primary/30 to-transparent pointer-events-none -z-10" />

                    <div className="w-24 h-24 rounded-full bg-slate-800 border-4 border-black shadow-[0_4px_15px_rgba(0,0,0,0.5)] flex items-center justify-center relative mb-4">
                        <User size={40} className="text-slate-400" />
                        <div className="absolute bottom-0 right-0 w-6 h-6 bg-green-500 rounded-full border-2 border-black flex items-center justify-center">
                            <div className="w-3 h-3 bg-white rounded-full animate-ping opacity-50 absolute" />
                        </div>
                    </div>

                    <h2 className="text-2xl font-black text-white">{rider.name || "Rider Test"}</h2>
                    <p className="text-sm font-medium text-slate-400 mb-4">{rider.phone || "555-000-0000"}</p>

                    <div className="flex gap-2 mb-2">
                        <span className="px-3 py-1 bg-black/50 border border-white/10 rounded-full text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-1">
                            {rider.vehicle?.toLowerCase() === "motorcycle" ? "Moto" : (rider.vehicle || "Moto")}
                        </span>
                        <span className="px-3 py-1 bg-black/50 border border-white/10 rounded-full text-xs font-black uppercase tracking-widest flex items-center gap-1 text-yellow-500">
                            <Star size={12} className="fill-yellow-500" /> {rider.rating?.toFixed(1) || "5.0"}
                        </span>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col items-center">
                        <span className="text-[10px] text-slate-400 font-black uppercase tracking-widest mb-1">Entregas</span>
                        <span className="text-2xl font-black text-white">{rider.completed_count || 0}</span>
                    </div>
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col items-center">
                        <span className="text-[10px] text-slate-400 font-black uppercase tracking-widest mb-1">Canceladas</span>
                        <span className="text-2xl font-black text-red-400">{rider.cancelled_count || 0}</span>
                    </div>
                </div>

                {/* Status Control */}
                <div>
                    <button
                        onClick={toggleStatus}
                        className={`w-full py-4 rounded-xl flex items-center justify-center gap-2 font-black uppercase tracking-widest transition-all ${rider.status === 'online' ? 'bg-green-500 hover:bg-green-600 shadow-[0_4px_15px_rgba(34,197,94,0.3)] text-black'
                            : 'bg-white/10 text-slate-300'
                            }`}
                    >
                        {rider.status === 'online' ? (
                            <>ESTÁS ONLINE (Conectado)</>
                        ) : (
                            <>Conectarse</>
                        )}
                    </button>
                    <p className="text-[10px] text-slate-500 font-medium text-center mt-3 mx-4 leading-relaxed tracking-wide">
                        Tu ubicación se comparte automáticamente cada 10s cuando estás online para recibir pedidos cercanos.
                    </p>
                </div>

                {/* Developer Options for demo */}
                <div className="pt-8">
                    <p className="text-[10px] uppercase font-black tracking-widest text-slate-600 text-center mb-4 border-b border-white/10 pb-4">Preferencias de Alertas</p>

                    <button
                        onClick={testSound}
                        className="w-full flex items-center justify-between p-4 bg-white/5 border border-white/5 hover:border-primary/20 rounded-2xl transition-all mb-3 text-slate-300 group"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <Volume2 size={16} className="text-primary" />
                            </div>
                            <div className="text-left">
                                <span className="text-sm font-bold block">Probar sonido de alerta</span>
                                <span className="text-[9px] text-slate-500 font-bold uppercase">CONEXO ALERT</span>
                            </div>
                        </div>
                        <Bell size={14} className="text-slate-500 group-hover:text-primary transition-colors" />
                    </button>

                    <p className="text-[10px] uppercase font-black tracking-widest text-slate-600 text-center mt-8 mb-4 border-b border-white/10 pb-4">Seguridad</p>

                    <button
                        onClick={handleToggleBiometry}
                        className={`w-full flex items-center justify-between p-4 bg-white/5 border transition-all rounded-2xl mb-3 text-slate-300 ${biometryEnabled ? 'border-primary/50' : 'border-white/5 hover:border-white/20'}`}
                    >
                        <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${biometryEnabled ? 'bg-primary/20' : 'bg-slate-500/10'}`}>
                                <ShieldCheck size={16} className={biometryEnabled ? 'text-primary' : 'text-slate-500'} />
                            </div>
                            <div className="text-left">
                                <span className={`text-sm font-bold block ${biometryEnabled ? 'text-white' : 'text-slate-400'}`}>Acceso Biométrico</span>
                                <span className={`text-[9px] font-black uppercase ${biometryEnabled ? 'text-primary' : 'text-slate-600'}`}>
                                    {biometrySupported ? (biometryEnabled ? 'Habilitado (FaceID/Huella)' : 'Tocar para configurar') : 'No compatible'}
                                </span>
                            </div>
                        </div>
                        <div className={`w-10 h-5 rounded-full relative transition-colors ${biometryEnabled ? 'bg-primary' : 'bg-white/10'}`}>
                            <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${biometryEnabled ? 'right-1' : 'left-1'}`} />
                        </div>
                    </button>

                    <Link to="/rider/jobs" className="flex items-center justify-between p-4 bg-white/5 border border-white/5 hover:border-white/20 rounded-2xl transition-all mb-3 text-slate-300 group">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center">
                                <Search size={16} className="text-blue-400" />
                            </div>
                            <span className="text-sm font-bold tracking-tight">Preferencias de zonas</span>
                        </div>
                        <div className="w-6 h-6 rounded-full border border-white/10 flex items-center justify-center group-hover:bg-white/10 transition-colors">
                            <MapPin size={12} className="text-slate-400" />
                        </div>
                    </Link>
                </div>
            </div>
        </RiderLayout>
    );
}
